import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';
import { api, extractApiError } from '../api/client';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510', padding: '1rem' }}>
        <div className="card" style={{ maxWidth: '24rem', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Invalid reset link</h2>
          <p style={{ color: '#8888aa', fontSize: 14, marginBottom: 24 }}>This password reset link is invalid or has expired.</p>
          <button onClick={() => navigate('/forgot-password')} className="btn-primary w-full">Request a new link</button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!password) errs.password = 'Password is required';
    else if (password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    setServerError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'INVALID_TOKEN' || apiErr.code === 'TOKEN_EXPIRED') {
        setServerError('This reset link has expired. Please request a new one.');
      } else {
        setServerError(apiErr.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '24rem' }}>
        <div className="flex flex-col items-center mb-8 gap-3">
          <LogoLockup size="lg" />
        </div>
        <div className="card">
          {success ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Password updated</h2>
              <p style={{ color: '#8888aa', fontSize: 14, marginBottom: 24 }}>Your password has been reset. You can now sign in.</p>
              <button onClick={() => navigate('/auth')} className="btn-primary w-full">Sign In</button>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Set a new password</h2>
              <p style={{ color: '#8888aa', fontSize: 14, marginBottom: 20 }}>Enter your new password below.</p>
              {serverError && (
                <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg px-3 py-2 text-red-400 text-sm">
                  {serverError}
                  {serverError.includes('expired') && (
                    <button type="button" onClick={() => navigate('/forgot-password')}
                      className="block mt-1 text-brand-400 hover:text-brand-300 text-xs font-medium">
                      Request a new link →
                    </button>
                  )}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">New Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => {
                      if (password && password.length < 8) setErrors(p => ({ ...p, password: 'At least 8 characters' }));
                      else setErrors(p => { const { password: _, ...r } = p; return r; });
                    }}
                    placeholder="Minimum 8 characters" className={`input-field ${errors.password ? 'error' : ''}`} autoComplete="new-password" />
                  {errors.password && <p className="error-text">{errors.password}</p>}
                </div>
                <div>
                  <label className="label">Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => {
                      if (password !== confirmPassword) setErrors(p => ({ ...p, confirmPassword: 'Passwords do not match' }));
                      else setErrors(p => { const { confirmPassword: _, ...r } = p; return r; });
                    }}
                    placeholder="Re-enter your password" className={`input-field ${errors.confirmPassword ? 'error' : ''}`} autoComplete="new-password" />
                  {errors.confirmPassword && <p className="error-text">{errors.confirmPassword}</p>}
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Resetting...</>
                  ) : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
